import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import { object } from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { TelescopeIcon } from '../../../../atoms/material-icons';
import RangeSelector from '../../../../atoms/range-slider';
import { fetchStockId } from '../../utils/marketsUtils';
import EmptyState from '../empty-state';
import './todays-price-stats.m.css';

const TodaysPriceStats = (props) => {
  const [overallPriceData, setOverallPriceData] = useState({});
  const [priceStats, setPriceStats] = useState({});
  const [nseSelected, setNseSelected] = useState(false);
  const [bseSelected, setBseSelected] = useState(false);
  const [stockId, setStockId] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);

  const getTodaysPriceStatsDataForQuery = async (stockId) => {
    const response = await fetch(`/next/feapi/stock/price-stats/${stockId}`);
    return response.json();
  };

  const resultPriceStatsData = useQuery(
    'getTodaysPriceStatsData',
    () => getTodaysPriceStatsDataForQuery(stockId),
    {
      refetchInterval: 30000,
      refetchIntervalInBackground: true,
      onSuccess: (data) => {
        setOverallPriceData(data?.data);
        nseSelected ? setPriceStats(data?.data?.nse) : setPriceStats(data?.data?.bse);
      },
      enabled: !!stockId,
    },
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const getTodaysPriceStats = async () => {
      const stockId = fetchStockId();
      setStockId(stockId);
      const response = await fetch(`/next/feapi/stock/price-stats/${stockId}`);
      const data = await response.json();
      setOverallPriceData(data?.data);
      setPriceStats(data?.data?.nse);
    };

    getTodaysPriceStats();
    setNseSelected(true);
    setBseSelected(false);
  }, []);

  useEffect(() => {
    if (resultPriceStatsData?.isSuccess) {
      setOverallPriceData(resultPriceStatsData?.data?.data);
      nseSelected
        ? setPriceStats(resultPriceStatsData?.data?.data?.nse)
        : setPriceStats(resultPriceStatsData?.data?.data?.bse);
    }
  }, [resultPriceStatsData?.data?.data]);

  const priceDiff = parseInt(priceStats?.currentPrice) - parseInt(priceStats?.previousClose);

  const nseClickHandler = () => {
    setPriceStats(overallPriceData?.nse);
    setNseSelected(true);
    setBseSelected(false);
  };

  const bseClickHandler = () => {
    setPriceStats(overallPriceData?.bse);
    setNseSelected(false);
    setBseSelected(true);
  };

  if (initialLoading) {
    return <div styleName="initial-loading-container"></div>;
  }

  return (
    <div styleName="price-stats-container" iconCode={'icon-ic_binoculars'}>
      <div styleName="header-title-container">
        <TelescopeIcon size={'26px'} color={'#d01e51'} />
        <h2 styleName="list-header-title">TODAY&#39;S PRICE STATS</h2>
      </div>
      <div styleName="btn-container">
        <button
          styleName={`price-stats-main-btn ${nseSelected ? 'selected' : ' '}`}
          onClick={nseClickHandler}
        >
          NSE
        </button>
        <button
          styleName={`price-stats-main-btn ${bseSelected ? 'selected' : ' '}`}
          onClick={bseClickHandler}
        >
          BSE
        </button>
      </div>
      {isEmpty(overallPriceData) || isEmpty(priceStats) ? (
        <div styleName="no-data-container-ext">
          <div styleName="no-data-container-int">
            <EmptyState subTextMessage={'for this stock at the moment'} />
          </div>
        </div>
      ) : (
        <>
          <div styleName="price-stats-header">
            <div>
              <span styleName="stat-name">
                {props?.currentStock?.aliases?.[0] ? props?.currentStock?.aliases?.[0] : '-'}
              </span>
            </div>
            <div styleName="price-stats-cmp">Rs. {priceStats?.currentPrice}</div>
            <div styleName="price-stats-time">
              As on {moment(priceStats?.date).format('MMM Do, hh:mm:ss')}
            </div>
            <div
              styleName="price-stats-price-change"
              style={{ color: priceDiff > 0 ? '#14c588' : 'red' }}
            >
              {priceDiff > 0 ? (
                <span>
                  +{priceDiff} &#9650; +
                  {isNaN(((priceDiff / parseInt(priceStats?.currentPrice)) * 100).toFixed(2))
                    ? 0
                    : ((priceDiff / parseInt(priceStats?.currentPrice)) * 100).toFixed(2)}
                  %
                </span>
              ) : (
                <span>
                  {priceDiff} &#9660;{' '}
                  {isNaN(((priceDiff / parseInt(priceStats?.currentPrice)) * 100).toFixed(2))
                    ? 0
                    : ((priceDiff / parseInt(priceStats?.currentPrice)) * 100).toFixed(2)}
                  %
                </span>
              )}
            </div>
          </div>
          <div styleName="price-stats-body">
            <table>
              <tr>
                <th>{nseSelected ? 'NSE STATS' : 'BSE STATS'}</th>
                <th>VALUES</th>
              </tr>
              <tr>
                <td>Volume Traded</td>
                <td>{priceStats?.volumeTraded}</td>
              </tr>
              <tr>
                <td>Weighted Avg. Price</td>
                <td>Rs. {priceStats?.weightedAveragePrice}</td>
              </tr>
              <tr>
                <td>Face Value</td>
                <td>Rs. {priceStats?.faceValue}</td>
              </tr>
              <tr>
                <td>Prev. Close</td>
                <td>Rs. {priceStats?.previousClose}</td>
              </tr>
              <tr>
                <td>Open Price</td>
                <td>Rs. {priceStats?.openPrice}</td>
              </tr>
              <tr>
                <td>Bid Price(Qty)</td>
                <td>
                  Rs. {priceStats?.bidPrice}({priceStats?.bidQuantity})
                </td>
              </tr>
              <tr>
                <td>Offer Price(Qty)</td>
                <td>
                  Rs. {priceStats?.offerPrice}({priceStats?.offerQuantity})
                </td>
              </tr>
            </table>
          </div>

          <div styleName="range-slider-container">
            <RangeSelector
              min={priceStats && priceStats['52WeekLow']}
              max={priceStats && priceStats['52WeekHigh']}
              currentValue={priceStats && priceStats?.currentPrice}
              label=""
              labelOne="52 Week Low"
              labelTwo="52 Week High"
            />

            <RangeSelector
              min={priceStats && priceStats?.low}
              max={priceStats && priceStats?.high}
              currentValue={priceStats && priceStats?.currentPrice}
              label=""
            />
          </div>
        </>
      )}
    </div>
  );
};

TodaysPriceStats.propTypes = {
  currentStock: object,
};

export default TodaysPriceStats;
